import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { FEATURE_KEYS, matchesPluginSlug } from 'src/utils/functions';
import pluginsService from 'src/services/pluginsService';
import { toast } from 'src/@/components/ui/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/@/components/ui/dialog';
import { Button } from 'src/@/components/ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from 'src/@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';
import PlatformChip from 'src/sections/@dashboard/marketing/components/PlatformChip';
import StatusChip from 'src/sections/@dashboard/marketing/components/StatusChip';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';
import GenericTable from 'src/components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from 'src/components/table/GenericTable.design';
import { CONTAS_SOCIAIS_DESIGN as d, configContaStatus } from './contasSociais.design';
import mktAccountsService from 'src/services/mktAccountsService';
import { PUBLISH_SCOPES } from 'src/sections/@dashboard/marketing/posts/constants';
import { ADS_SCOPES } from 'src/sections/@dashboard/marketing/anuncios/constants';
import { META_ADS_DISPONIVEL } from 'src/utils/metaAppReview';
import {
    abrirMetaOAuth, abrirInstagramOAuth, consumeOAuthReturnPath,
    unirEscopos, escoposConcedidos,
    ESCOPOS_MESSENGER, ESCOPOS_FACEBOOK, ESCOPOS_ANUNCIOS,
    ESCOPOS_DIRECT_INSTAGRAM, ESCOPOS_INSTAGRAM,
    ESCOPOS_FACEBOOK_TODOS,
} from './metaOAuth';
import { abrirMercadoLivreOAuth } from './mercadoLivreOAuth';
import MetaAccountSelect from './MetaAccountSelect';
import LinkChannelDialog, { CANAL_PLATFORM_POR_PROVIDER } from './LinkChannelDialog';

const PLUGINS_PATH = '/dashboard/configuracoes/plugins';
const CANAIS_PATH = '/dashboard/configuracoes/canais';

const PLATFORM_ICON = {
    facebook: { icon: 'mdi:facebook', className: 'text-blue-600 bg-card' },
    instagram: { icon: 'mdi:instagram', className: 'text-pink-600 bg-card' },
    meta_ads: { icon: 'mdi:facebook', className: 'text-violet-600 bg-card' },
    whatsapp_business: { icon: 'mdi:whatsapp', className: 'text-green-600 bg-card' },
    mercado_livre: { icon: 'arcticons:mercado-livre', className: 'text-yellow-500 bg-card' }
};

// Embedded Signup do WhatsApp: mesma tela do canal, sem channel_id — o
// resultado vira conta social, nao credencial de canal.
function abrirWhatsappSignup(navigate) {
    const params = new URLSearchParams({
        app_id: import.meta.env.VITE_FACEBOOK_APP_ID || '1553356899550775',
        config_id: import.meta.env.VITE_FACEBOOK_CONFIG_ID || '4671161989780637',
        return_to: 'contas-sociais'
    });
    navigate(`/dashboard/configuracoes/facebook-connect?${params.toString()}`);
}

// WhatsApp nao publica post nem le anuncio: as regras de escopo do Facebook
// (publicacao, ads_read) nao se aplicam. Sem esta excecao a conta cairia em
// "somente leitura" e ofereceria o botao de OAuth de Paginas, que e' o fluxo
// errado.
function isWhatsapp(account) {
    return account.provider === 'whatsapp_business';
}

// Mesma excecao do WhatsApp, pelo mesmo motivo: o Mercado Livre e canal de
// atendimento (responde pergunta de anuncio e mensagem de pos-venda), nao publica
// post. Sem isto ele cai no balde readOnlyAccounts e a tela — compartilhada com as
// contas Meta — mostra o alerta de "publicacao bloqueada" e um CTA que chama
// liberarPublicacao, ou seja, o OAuth do Instagram para uma conta que nem e da Meta.
function isSemPublicacao(account) {
    return isWhatsapp(account) || account.provider === 'mercado_livre';
}

function perfilPath(account) {
    if (!account?._id) return null;
    if (account.provider === 'whatsapp_business') {
        return `/dashboard/configuracoes/whatsapp-perfil?accountId=${account._id}`;
    }
    if (account.provider === 'facebook') {
        return `/dashboard/configuracoes/facebook-perfil?accountId=${account._id}`;
    }
    if (account.provider === 'instagram') {
        return `/dashboard/configuracoes/instagram-perfil?accountId=${account._id}`;
    }
    return null;
}

function canOpenProfile(account) {
    if (!perfilPath(account)) return false;
    if (isConexaoIncompleta(account)) return false;
    if (account.status === 'disconnected' || account.status === 'pending') return false;
    return true;
}

// Conta cujo Embedded Signup parou no meio (sem numero ou sem PIN). Fica
// visivel para o usuario concluir ou descartar, mas nao pode ser vinculada.
function isConexaoIncompleta(account) {
    return isWhatsapp(account) && account.status === 'pending';
}

const COEXISTENCE_SYNC_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Status da janela Meta de 24h para sync SMB no fluxo Contas Sociais. */
function coexistenceLinkWindow(account) {
    if (!isWhatsapp(account) || account.account_info?.signupMode !== 'coexistence') {
        return null;
    }
    const coex = account.account_info?.coexistence || {};
    const contactsDone = Boolean(coex.contacts?.requestId);
    const historyDone = Boolean(coex.history?.requestId);
    if (contactsDone && historyDone) {
        return { state: 'synced', deadline: null };
    }
    if (account.canal_vinculado && !coex.pendingChannelLink) {
        return { state: 'linked', deadline: coex.syncDeadlineAt ? new Date(coex.syncDeadlineAt) : null };
    }
    let deadline = null;
    if (coex.syncDeadlineAt) deadline = new Date(coex.syncDeadlineAt);
    else if (coex.onboardedAt) {
        deadline = new Date(new Date(coex.onboardedAt).getTime() + COEXISTENCE_SYNC_WINDOW_MS);
    }
    if (!deadline || Number.isNaN(deadline.getTime())) {
        return { state: 'pending_link', deadline: null };
    }
    if (Date.now() > deadline.getTime()) {
        return { state: 'expired', deadline };
    }
    return { state: 'pending_link', deadline };
}

function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return String(num);
}

function formatDate(dateStr, t, locale) {
    if (!dateStr) return t('socialAccounts.card.neverSynced');
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale || 'pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const SCOPES_PENDENTES_NO_APP = ['instagram_manage_insights', 'read_insights'];

function missingPublishScopes(account) {
    if (account.provider === 'meta_ads' || isSemPublicacao(account)) return [];
    const required = PUBLISH_SCOPES[account.provider] || [];
    const scopes = account.scopes || [];
    return required.filter((s) => !scopes.includes(s));
}

function canPublish(account) {
    if (account.provider === 'meta_ads' || isSemPublicacao(account)) return false;
    const required = PUBLISH_SCOPES[account.provider] || [];
    return required.length > 0 && missingPublishScopes(account).length === 0;
}

// Escopos de anuncios sao no nivel do usuario (Facebook Login).
function missingAdsScopes(account) {
    if (account.provider === 'instagram' || isWhatsapp(account)) return [];
    if (account.provider !== 'facebook' && account.provider !== 'meta_ads') return [];
    const scopes = account.scopes || [];
    return ADS_SCOPES.filter((s) => !scopes.includes(s));
}

function canReadAds(account) {
    return account.provider === 'meta_ads' && missingAdsScopes(account).length === 0;
}

function blockedByAppReview(account) {
    const missing = account.provider === 'meta_ads'
        ? missingAdsScopes(account)
        : missingPublishScopes(account);
    return missing.length > 0 && missing.every((s) => SCOPES_PENDENTES_NO_APP.includes(s));
}

// View-model unico consumido por AccountCard (grade) e AccountRow (lista) —
// evita computar as mesmas regras de negocio duas vezes por conta.
function buildAccountView(account, t) {
    return {
        account,
        isAds: account.provider === 'meta_ads',
        isWa: isWhatsapp(account),
        waIncompleto: isConexaoIncompleta(account),
        coexWindow: coexistenceLinkWindow(account),
        followers: account.account_info?.followers_count || account.account_info?.fans_count || 0,
        posts: account.account_info?.media_count || 0,
        publish: canPublish(account),
        adsOk: canReadAds(account),
        canalVinculado: account.canal_vinculado,
        platformIcon: PLATFORM_ICON[account.provider],
        adsScopesMissing: missingAdsScopes(account),
        profileHref: canOpenProfile(account) ? perfilPath(account) : null,
        statusCfg: configContaStatus(account.status, t),
    };
}

// Coluna "Canal" (grade e lista): unico lugar que decide o texto/tom de
// vinculo de canal a partir do estado real da conta.
function getCanalDisplay(view, t) {
    const { isAds, waIncompleto, coexWindow, canalVinculado } = view;
    if (isAds) return { title: '—', titleClass: 'text-muted-foreground', caption: null };
    if (waIncompleto) return { title: t('socialAccounts.canal.incomplete'), titleClass: 'text-amber-700 dark:text-amber-400', caption: t('socialAccounts.canal.incompleteCaption') };
    if (coexWindow?.state === 'expired') return { title: t('socialAccounts.canal.windowExpired'), titleClass: 'text-red-600 dark:text-red-400', caption: t('socialAccounts.canal.windowExpiredCaption') };
    if (coexWindow?.state === 'pending_link') return { title: t('socialAccounts.canal.noChannel'), titleClass: 'text-amber-700 dark:text-amber-400', caption: t('socialAccounts.canal.pendingLinkCaption') };
    if (canalVinculado) return { title: canalVinculado.nome, titleClass: 'text-foreground', caption: t('socialAccounts.canal.linkedCaption') };
    return { title: t('socialAccounts.canal.noChannel'), titleClass: 'text-amber-700 dark:text-amber-400', caption: t('socialAccounts.canal.noChannelCaption') };
}

// Botao principal (grade e lista): uma unica acao, priorizada pelo estado
// mais urgente da conta. Casos ja cobertos por um bloco explicativo proprio
// no card (waIncompleto/coexWindow/escopos) reaproveitam a mesma acao aqui.
function getPrimaryAction(view, { navigate, oauth, onLinkChannel, t }) {
    const { account, isAds, isWa, waIncompleto, coexWindow, publish, adsOk, canalVinculado } = view;
    if (isWa) {
        if (waIncompleto) return { label: t('socialAccounts.actions.completeConnection'), icon: 'eva:refresh-outline', onClick: () => abrirWhatsappSignup(navigate) };
        if (coexWindow?.state === 'expired') return { label: t('socialAccounts.actions.reconnect'), icon: 'eva:refresh-outline', onClick: () => abrirWhatsappSignup(navigate) };
        if (coexWindow?.state === 'pending_link') return { label: t('socialAccounts.actions.linkChannel'), icon: 'eva:link-2-outline', onClick: () => onLinkChannel(account) };
        if (!canalVinculado) return { label: t('socialAccounts.actions.linkChannel'), icon: 'eva:link-2-outline', onClick: () => onLinkChannel(account) };
        return null;
    }
    if (isAds) {
        // Sem a permissao liberada na Meta o botao nao teria como funcionar —
        // reconectar devolve o mesmo token sem ads_read/pages_manage_ads.
        if (!adsOk && META_ADS_DISPONIVEL) return { label: t('socialAccounts.actions.updatePermissions'), icon: 'eva:unlock-outline', onClick: oauth.conectarAnuncios, tone: 'warning' };
        return null;
    }
    if (!publish) {
        if (blockedByAppReview(account)) return null;
        return { label: t('socialAccounts.actions.unlockPublish'), icon: 'eva:unlock-outline', onClick: () => oauth.liberarPublicacao(account), tone: 'warning' };
    }
    if (!canalVinculado) return { label: t('socialAccounts.actions.linkChannel'), icon: 'eva:link-2-outline', onClick: () => onLinkChannel(account) };
    return null;
}

function AlertRow({ tone, title, subtitle, ctaLabel, ctaIcon, onCta }) {
    return (
        <div className={d.alertRow.container}>
            <span className={`${d.alertRow.bar} ${tone === 'urgent' ? d.alertRow.barUrgent : d.alertRow.barWarning}`} />
            <div className={d.alertRow.body}>
                <p className={d.alertRow.title}>{title}</p>
                {subtitle && <p className={d.alertRow.subtitle}>{subtitle}</p>}
            </div>
            {ctaLabel && (
                <button type="button" className={d.alertRow.btn} onClick={onCta}>
                    {ctaIcon && <Iconify icon={ctaIcon} width={14} />}
                    {ctaLabel}
                </button>
            )}
        </div>
    );
}

// Menu de conexao: mesmas opcoes do header, reaproveitado tambem no estado
// vazio (empty state deve oferecer exatamente o que o dropdown oferece,
// nao um subconjunto simplificado).
function extractInstalledSlugs(payload) {
    const list = [payload?.data?.data, payload?.data, payload].find(Array.isArray) || [];
    return new Set(
        list
            .filter((item) => item && item.installed === true && item.slug)
            .map((item) => String(item.slug))
    );
}

function canUsePlugin(installedSlugs, slug) {
    return matchesPluginSlug(installedSlugs, slug);
}

// Vale para o dropdown e para os cards do empty state — as duas listas usam a
// mesma regra: plugin contratado pelo cliente E permissao concedida ao app na
// Meta (requerAnuncios). O nome do campo do slug difere entre as listas.
function opcaoDisponivel(item, installedSlugs) {
    if (item.requerAnuncios && !META_ADS_DISPONIVEL) return false;
    return canUsePlugin(installedSlugs, item.slug || item.plugin);
}

const CONNECT_OPTIONS = [
    { key: 'messenger', slug: FEATURE_KEYS.MESSENGER, icon: 'mdi:facebook-messenger', iconClass: 'mt-0.5 text-[#1877f2]', titleKey: 'socialAccounts.menu.messenger', descKey: 'socialAccounts.menu.messengerDesc', action: 'conectarMessenger' },
    { key: 'directIg', slug: FEATURE_KEYS.INSTAGRAM_DIRECT, icon: 'mdi:instagram', iconClass: 'mt-0.5 text-pink-600', titleKey: 'socialAccounts.menu.directIg', descKey: 'socialAccounts.menu.directIgDesc', action: 'conectarDirectInstagram' },
    { key: 'instagram', slug: FEATURE_KEYS.MARKETING_POSTS, icon: 'skill-icons:instagram', iconClass: 'mt-0.5', titleKey: 'socialAccounts.menu.instagram', descKey: 'socialAccounts.menu.instagramDesc', action: 'conectarInstagram' },
    // Anuncios pede o grupo ESCOPOS_ANUNCIOS, que nenhuma outra opcao cobre:
    // sem esta entrada, quem chega de Marketing -> Anuncios nao tem por onde
    // conceder a permissao (o icone/cor sao os mesmos do PROVIDER_UI.meta_ads).
    { key: 'ads', requerAnuncios: true, slug: FEATURE_KEYS.MARKETING_ADS, icon: 'mdi:facebook', iconClass: 'mt-0.5 text-violet-600', titleKey: 'socialAccounts.menu.ads', descKey: 'socialAccounts.menu.adsDesc', action: 'conectarAnuncios' },
    { key: 'whatsapp', slug: 'whatsapp-business', icon: 'mdi:whatsapp', iconClass: 'mt-0.5 text-green-600', titleKey: 'socialAccounts.menu.whatsapp', descKey: 'socialAccounts.menu.whatsappDesc', action: 'whatsapp' },
    { key: 'telegram', slug: FEATURE_KEYS.TELEGRAM, icon: 'logos:telegram', iconClass: 'mt-0.5', titleKey: 'socialAccounts.menu.telegram', descKey: 'socialAccounts.menu.telegramDesc', action: 'telegram' },
    // Ao contrário do Telegram (que manda para o wizard de canal, porque o token é
    // colado lá), o Mercado Livre tem OAuth — conecta a conta aqui mesmo, e o
    // vínculo com o canal é um segundo passo.
    { key: 'mercadoLivre', slug: FEATURE_KEYS.MERCADO_LIVRE, icon: 'arcticons:mercado-livre', iconClass: 'mt-0.5 text-yellow-500', titleKey: 'socialAccounts.menu.mercadoLivre', descKey: 'socialAccounts.menu.mercadoLivreDesc', action: 'conectarMercadoLivre' },
];

function toInstalledSlugSet(list) {
    if (!Array.isArray(list)) return null;
    return new Set(list.map(String));
}

function ConnectAccountMenu({ t, navigate, oauthActions, triggerClassName, align = 'end', installedSlugs, pluginsLoading }) {
    if (pluginsLoading) {
        return (
            <button type="button" className={triggerClassName} disabled>
                {t('common:loading', 'Carregando...')}
            </button>
        );
    }

    const visibleOptions = CONNECT_OPTIONS.filter((option) => opcaoDisponivel(option, installedSlugs));
    const actionMap = {
        ...oauthActions,
        whatsapp: () => abrirWhatsappSignup(navigate),
        telegram: () => navigate(`${CANAIS_PATH}?novo=telegram`),
    };

    if (visibleOptions.length === 0) {
        return (
            <button type="button" className={triggerClassName} onClick={() => navigate(PLUGINS_PATH)}>
                Instalar plugins
            </button>
        );
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button type="button" className={triggerClassName}>
                    <span>{t('socialAccounts.header.connectAccount')}</span>
                    <Iconify icon="eva:chevron-down-fill" width={16} />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={align} className="w-72 rounded-xl">
                {visibleOptions.map((option) => (
                    <DropdownMenuItem
                        key={option.key}
                        onClick={actionMap[option.action]}
                        className="items-start gap-2"
                    >
                        <Iconify icon={option.icon} width={16} className={option.iconClass} />
                        <span>
                            <span className="block text-sm">{t(option.titleKey)}</span>
                            <span className="block text-xs text-muted-foreground">{t(option.descKey)}</span>
                        </span>
                    </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={oauthActions.atualizarPermissoesFacebook} className="items-start gap-2">
                    <Iconify icon="eva:refresh-outline" width={16} className="mt-0.5 text-muted-foreground" />
                    <span>
                        <span className="block text-sm">{t('socialAccounts.menu.updatePermissions')}</span>
                        <span className="block text-xs text-muted-foreground">{t('socialAccounts.menu.updatePermissionsDesc')}</span>
                    </span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// Um card por opcao do dropdown "Conectar Conta" (+ Site, que nao esta no
// dropdown mas e' um canal valido). "Atualizar permissoes" fica de fora: so
// faz sentido para quem ja tem conta conectada, nao existe ainda aqui.
const EMPTY_STATE_TONE = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400',
    pink: 'bg-pink-50 text-pink-600 dark:bg-pink-500/15 dark:text-pink-400',
    green: 'bg-green-50 text-green-600 dark:bg-green-500/15 dark:text-green-400',
    violet: 'bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400',
    sky: 'bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
};

const EMPTY_STATE_GROUPS = [
    {
        id: 'atendimento',
        titleKey: 'socialAccounts.onboarding.groups.atendimentoTitle',
        descKey: 'socialAccounts.onboarding.groups.atendimentoDesc',
        icon: 'eva:message-circle-outline',
    },
    {
        id: 'marketing',
        titleKey: 'socialAccounts.onboarding.groups.marketingTitle',
        descKey: 'socialAccounts.onboarding.groups.marketingDesc',
        icon: 'eva:trending-up-outline',
    },
    {
        id: 'site',
        titleKey: 'socialAccounts.onboarding.groups.siteTitle',
        descKey: 'socialAccounts.onboarding.groups.siteDesc',
        icon: 'eva:globe-2-outline',
    },
];

const EMPTY_STATE_CARDS = [
    { key: 'whatsapp', group: 'atendimento', plugin: 'whatsapp-business', icon: 'mdi:whatsapp', tone: 'green', titleKey: 'socialAccounts.onboarding.whatsappTitle', benefitKey: 'socialAccounts.onboarding.whatsappBenefit', requirementKey: 'socialAccounts.onboarding.whatsappRequirement', ctaKey: 'socialAccounts.menu.whatsapp', action: 'whatsapp' },
    { key: 'messenger', group: 'atendimento', plugin: FEATURE_KEYS.MESSENGER, icon: 'mdi:facebook-messenger', tone: 'blue', titleKey: 'socialAccounts.onboarding.messengerTitle', benefitKey: 'socialAccounts.onboarding.messengerBenefit', requirementKey: 'socialAccounts.onboarding.messengerRequirement', ctaKey: 'socialAccounts.menu.messenger', action: 'conectarMessenger' },
    { key: 'directIg', group: 'atendimento', plugin: FEATURE_KEYS.INSTAGRAM_DIRECT, icon: 'mdi:instagram', tone: 'pink', titleKey: 'socialAccounts.onboarding.directIgTitle', benefitKey: 'socialAccounts.onboarding.directIgBenefit', requirementKey: 'socialAccounts.onboarding.directIgRequirement', ctaKey: 'socialAccounts.menu.directIg', action: 'conectarDirectInstagram' },
    { key: 'telegram', group: 'atendimento', plugin: FEATURE_KEYS.TELEGRAM, icon: 'logos:telegram', tone: 'sky', titleKey: 'socialAccounts.onboarding.telegramTitle', benefitKey: 'socialAccounts.onboarding.telegramBenefit', requirementKey: 'socialAccounts.onboarding.telegramRequirement', ctaKey: 'socialAccounts.menu.telegram', action: 'telegram' },
    { key: 'mercadoLivre', group: 'atendimento', plugin: FEATURE_KEYS.MERCADO_LIVRE, icon: 'arcticons:mercado-livre', tone: 'amber', titleKey: 'socialAccounts.onboarding.mercadoLivreTitle', benefitKey: 'socialAccounts.onboarding.mercadoLivreBenefit', requirementKey: 'socialAccounts.onboarding.mercadoLivreRequirement', ctaKey: 'socialAccounts.menu.mercadoLivre', action: 'conectarMercadoLivre' },
    { key: 'instagram', group: 'marketing', plugin: FEATURE_KEYS.MARKETING_POSTS, icon: 'skill-icons:instagram', tone: 'pink', titleKey: 'socialAccounts.onboarding.instagramTitle', benefitKey: 'socialAccounts.onboarding.instagramBenefit', requirementKey: 'socialAccounts.onboarding.instagramRequirement', ctaKey: 'socialAccounts.menu.instagram', action: 'conectarInstagram' },
    { key: 'ads', group: 'marketing', requerAnuncios: true, plugin: FEATURE_KEYS.MARKETING_ADS, icon: 'mdi:facebook', tone: 'violet', titleKey: 'socialAccounts.onboarding.adsTitle', benefitKey: 'socialAccounts.onboarding.adsBenefit', requirementKey: 'socialAccounts.onboarding.adsRequirement', ctaKey: 'socialAccounts.menu.ads', action: 'conectarAnuncios' },
    { key: 'site', group: 'site', plugin: FEATURE_KEYS.WEBCHAT, icon: 'streamline-plump:web', tone: 'violet', titleKey: 'socialAccounts.onboarding.siteTitle', benefitKey: 'socialAccounts.onboarding.siteBenefit', requirementKey: 'socialAccounts.onboarding.siteRequirement', ctaKey: 'socialAccounts.onboarding.siteCta', action: 'site' },
];

const ONBOARDING_STEPS = [
    { num: 1, titleKey: 'socialAccounts.onboarding.step1Title' },
    { num: 2, titleKey: 'socialAccounts.onboarding.step2Title' },
    { num: 3, titleKey: 'socialAccounts.onboarding.step3Title' },
];

function AccountsEmptyState({ t, navigate, oauthActions, installedSlugs, pluginsLoading }) {
    const actionMap = {
        ...oauthActions,
        whatsapp: () => abrirWhatsappSignup(navigate),
        telegram: () => navigate(`${CANAIS_PATH}?novo=telegram`),
        site: () => navigate(CANAIS_PATH),
    };

    if (pluginsLoading) {
        return <MktLoading />;
    }

    const cards = EMPTY_STATE_CARDS.filter((card) => opcaoDisponivel(card, installedSlugs));
    const groups = EMPTY_STATE_GROUPS
        .map((group) => ({
            ...group,
            cards: cards.filter((card) => card.group === group.id),
        }))
        .filter((group) => group.cards.length > 0);
    const mostraAjudaInstagram = cards.some((c) => c.key === 'directIg') && cards.some((c) => c.key === 'instagram');

    return (
        <div>
            <div className={d.emptyState.header}>
                <div className={d.emptyState.iconBox}>
                    <Iconify icon="eva:link-2-outline" width={22} className="text-violet-600 dark:text-violet-400" />
                </div>
                <h2 className={d.emptyState.heading}>
                    {cards.length === 0
                        ? t('socialAccounts.onboarding.noPluginsTitle')
                        : t('socialAccounts.onboarding.heading')}
                </h2>
                <p className={d.emptyState.subheading}>
                    {cards.length === 0
                        ? t('socialAccounts.onboarding.noPluginsDesc')
                        : t('socialAccounts.onboarding.subheading')}
                </p>
            </div>

            {cards.length === 0 ? (
                <div className="flex justify-center mt-8">
                    <button type="button" className={d.pageHeader.actionBtn} onClick={() => navigate(PLUGINS_PATH)}>
                        {t('socialAccounts.onboarding.noPluginsCta')}
                    </button>
                </div>
            ) : (
                <>
                    <div className={d.emptyState.steps}>
                        {ONBOARDING_STEPS.map((step) => (
                            <div key={step.num} className={d.emptyState.step}>
                                <span className={d.emptyState.stepNum}>{step.num}</span>
                                <span className={d.emptyState.stepTitle}>{t(step.titleKey)}</span>
                            </div>
                        ))}
                    </div>

                    {groups.map((group) => (
                        <section key={group.id} className={d.emptyState.groupSection}>
                            <div className={d.emptyState.groupHeader}>
                                <div className={d.emptyState.groupIcon}>
                                    <Iconify icon={group.icon} width={16} />
                                </div>
                                <div className="min-w-0">
                                    <h3 className={d.emptyState.groupTitle}>{t(group.titleKey)}</h3>
                                    <p className={d.emptyState.groupDesc}>{t(group.descKey)}</p>
                                </div>
                            </div>

                            <div className={d.emptyState.list}>
                                {group.cards.map((card) => (
                                    <div key={card.key} className={d.emptyState.card}>
                                        <div className={`${d.emptyState.cardIconBox} ${EMPTY_STATE_TONE[card.tone]}`}>
                                            <Iconify icon={card.icon} width={18} />
                                        </div>
                                        <div className={d.emptyState.cardBody}>
                                            <h4 className={d.emptyState.cardTitle}>{t(card.titleKey)}</h4>
                                            <p className={d.emptyState.cardBenefit}>{t(card.benefitKey)}</p>
                                            <p className={d.emptyState.cardRequirement} title={t(card.requirementKey)}>
                                                {t(card.requirementKey)}
                                            </p>
                                        </div>
                                        <button type="button" className={d.emptyState.cardCta} onClick={actionMap[card.action]}>
                                            {t('socialAccounts.onboarding.connectShort')}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}

                    {mostraAjudaInstagram && (
                        <div className={d.emptyState.helpBox}>
                            <Iconify icon="eva:question-mark-circle-outline" width={16} className="text-violet-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className={d.emptyState.helpTitle}>{t('socialAccounts.onboarding.instagramHelpTitle')}</p>
                                <p className={d.emptyState.helpText}>{t('socialAccounts.onboarding.instagramHelpText')}</p>
                            </div>
                        </div>
                    )}
                </>
            )}

            {cards.length > 0 && (
                <div className={d.emptyState.footerHint}>
                    <Iconify icon="eva:info-outline" width={14} className="flex-shrink-0 mt-0.5" />
                    {t('socialAccounts.onboarding.footerHint')}
                </div>
            )}
        </div>
    );
}

function AccountCard({ view, navigate, oauth, onLinkChannel, syncingId, onSync, onDisconnect }) {
    const { t, i18n } = useTranslation('settings');
    const {
        account, isAds, isWa, waIncompleto, coexWindow, followers, posts, publish, adsOk,
        canalVinculado, platformIcon, adsScopesMissing, profileHref, statusCfg,
    } = view;

    const primary = getPrimaryAction(view, { navigate, oauth, onLinkChannel, t });
    const showProfileBtn = Boolean(profileHref);
    const showPostsBtn = !isAds && !isWa;

    return (
        <div
            className={`${d.accounts.card}${profileHref ? ' cursor-pointer' : ''}`}
            onClick={profileHref ? () => navigate(profileHref) : undefined}
            role={profileHref ? 'button' : undefined}
            tabIndex={profileHref ? 0 : undefined}
            onKeyDown={profileHref
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(profileHref);
                    }
                }
                : undefined}
        >
            <div className={d.accounts.cardHeader}>
                <div className="relative flex-shrink-0">
                    {account.profile_picture ? (
                        <img src={account.profile_picture} alt={account.account_name} className={d.accounts.avatar} />
                    ) : isAds ? (
                        <div className={d.accounts.avatarFallback}>
                            <Iconify icon="eva:pricetags-outline" width={24} className="text-muted-foreground" />
                        </div>
                    ) : (
                        <div className={d.accounts.avatarLetter}>
                            {(account.account_name || '?').charAt(0).toUpperCase()}
                        </div>
                    )}
                    {platformIcon && (
                        <span className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full shadow-sm flex items-center justify-center ${platformIcon.className}`}>
                            <Iconify icon={platformIcon.icon} width={13} />
                        </span>
                    )}
                </div>
                <div className={d.accounts.cardInfo}>
                    <p className={d.accounts.cardName}>{account.account_name}</p>
                    <p className={d.accounts.cardUsername}>
                        {isAds
                            ? (account.account_info?.act_id || `act_${account.account_id}`)
                            : isWa
                              ? (account.account_info?.displayPhoneNumber || t('socialAccounts.card.pendingNumber'))
                              : (account.account_info?.username ? `@${account.account_info.username}` : account.account_info?.category || account.provider)}
                    </p>
                </div>
                <StatusChip tone={statusCfg.tone} label={statusCfg.label} size="sm" />
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
                <PlatformChip platform={account.provider} />
                {isWa ? (
                    waIncompleto ? (
                        <span className={d.capability.readOnly}>
                            <Iconify icon="eva:clock-outline" width={11} />
                            {t('socialAccounts.capability.incomplete')}
                        </span>
                    ) : (
                        <span className={d.capability.publish}>
                            <Iconify icon="eva:message-circle-outline" width={11} />
                            {t('socialAccounts.capability.messagingEnabled')}
                        </span>
                    )
                ) : isAds ? (
                    adsOk ? (
                        <span className={d.capability.publish}>
                            <Iconify icon="eva:bar-chart-outline" width={11} />
                            {t('socialAccounts.capability.adsEnabled')}
                        </span>
                    ) : (
                        <span className={d.capability.readOnly}>
                            <Iconify icon="eva:eye-outline" width={11} />
                            {t('socialAccounts.capability.adsIncomplete')}
                        </span>
                    )
                ) : publish ? (
                    <span className={d.capability.publish}>
                        <Iconify icon="eva:paper-plane-outline" width={11} />
                        {t('socialAccounts.capability.publishEnabled')}
                    </span>
                ) : (
                    <span className={d.capability.readOnly}>
                        <Iconify icon="eva:eye-outline" width={11} />
                        {t('socialAccounts.capability.readOnly')}
                    </span>
                )}
                {META_ADS_DISPONIVEL && !isAds && account.provider === 'facebook' && adsScopesMissing.length > 0 && (
                    <span className={d.capability.readOnly}>
                        <Iconify icon="eva:alert-triangle-outline" width={11} />
                        {t('socialAccounts.capability.noAdsPermission')}
                    </span>
                )}
                {!isAds && !waIncompleto && (canalVinculado ? (
                    <span className={d.capability.canalChip}>
                        <Iconify icon="eva:link-2-outline" width={10} />
                        {canalVinculado.nome}
                    </span>
                ) : (
                    <span className={d.capability.canalChip}>
                        <Iconify icon="eva:alert-triangle-outline" width={10} />
                        {t('socialAccounts.capability.noChannel')}
                    </span>
                ))}
                {coexWindow?.state === 'pending_link' && (
                    <span className={d.capability.readOnly}>
                        <Iconify icon="eva:clock-outline" width={11} />
                        {t('socialAccounts.capability.linkWithin24h')}
                    </span>
                )}
                {coexWindow?.state === 'expired' && (
                    <span className={d.capability.readOnly}>
                        <Iconify icon="eva:alert-circle-outline" width={11} />
                        {t('socialAccounts.capability.syncWindowExpired')}
                    </span>
                )}
            </div>

            <div className={d.accounts.cardStats}>
                {isWa ? (
                    <>
                        <div className={d.accounts.cardStat}>
                            <p className={d.accounts.cardStatValue}>
                                {account.account_info?.signupMode === 'coexistence' ? t('socialAccounts.stats.coexistence') : t('socialAccounts.stats.cloudApi')}
                            </p>
                            <p className={d.accounts.cardStatLabel}>{t('socialAccounts.stats.mode')}</p>
                        </div>
                        <div className={d.accounts.cardStat}>
                            <p className={d.accounts.cardStatValue}>
                                {account.account_info?.wabaId || '—'}
                            </p>
                            <p className={d.accounts.cardStatLabel}>{t('socialAccounts.stats.waba')}</p>
                        </div>
                    </>
                ) : isAds ? (
                    <>
                        <div className={d.accounts.cardStat}>
                            <p className={d.accounts.cardStatValue}>{account.account_info?.currency || 'BRL'}</p>
                            <p className={d.accounts.cardStatLabel}>{t('socialAccounts.stats.currency')}</p>
                        </div>
                        <div className={d.accounts.cardStat}>
                            <p className={d.accounts.cardStatValue}>
                                {formatNumber(Number(account.account_info?.amount_spent || 0) / 100)}
                            </p>
                            <p className={d.accounts.cardStatLabel}>{t('socialAccounts.stats.totalSpend')}</p>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={d.accounts.cardStat}>
                            <p className={d.accounts.cardStatValue}>{formatNumber(followers)}</p>
                            <p className={d.accounts.cardStatLabel}>{t('socialAccounts.stats.followers')}</p>
                        </div>
                        <div className={d.accounts.cardStat}>
                            <p className={d.accounts.cardStatValue}>{formatNumber(posts)}</p>
                            <p className={d.accounts.cardStatLabel}>{t('socialAccounts.stats.posts')}</p>
                        </div>
                    </>
                )}
            </div>

            <div className={d.accounts.cardFooter} onClick={(e) => e.stopPropagation()}>
                <span className={d.accounts.syncText}>
                    {t('socialAccounts.card.synced', { date: formatDate(account.last_sync, t, i18n.language) })}
                </span>
                <div className={d.accounts.actionsRow}>
                    {primary && (
                        <button
                            type="button"
                            className={primary.tone === 'warning' ? d.accounts.warnBtn : d.accounts.primaryBtn}
                            onClick={primary.onClick}
                        >
                            <Iconify icon={primary.icon} width={14} />
                            {primary.label}
                        </button>
                    )}
                    {showProfileBtn && (
                        <button type="button" className={d.accounts.profileBtn} onClick={() => navigate(profileHref)}>
                            <Iconify icon="eva:edit-2-outline" width={14} />
                            {t('socialAccounts.actions.profile')}
                        </button>
                    )}
                    {showPostsBtn && (
                        <button type="button" className={d.accounts.profileBtn} onClick={() => navigate('/dashboard/marketing/publicacoes')}>
                            <Iconify icon="eva:paper-plane-outline" width={14} />
                            {t('socialAccounts.header.goToPosts')}
                        </button>
                    )}
                    <button
                        type="button"
                        title={t('socialAccounts.actions.syncNow')}
                        className={d.accounts.iconBtn}
                        onClick={() => onSync(account._id)}
                        disabled={syncingId === account._id}
                    >
                        <Iconify icon={syncingId === account._id ? 'eos-icons:loading' : 'eva:refresh-outline'} width={15} />
                    </button>
                    <button
                        type="button"
                        title={t('socialAccounts.actions.disconnect')}
                        className={d.accounts.iconBtnDanger}
                        onClick={() => onDisconnect(account)}
                    >
                        <Iconify icon="eva:close-circle-outline" width={16} />
                    </button>
                </div>
            </div>

            {waIncompleto && (
                <div className={d.capability.unlockRow} onClick={(e) => e.stopPropagation()}>
                    <p className={d.capability.unlockText}>
                        {t('socialAccounts.card.incompleteHint')}
                    </p>
                    <button type="button" className={d.capability.unlockBtn} onClick={() => abrirWhatsappSignup(navigate)}>
                        <Iconify icon="eva:refresh-outline" width={13} />
                        {t('socialAccounts.actions.completeConnection')}
                    </button>
                </div>
            )}
            {coexWindow?.state === 'pending_link' && !waIncompleto && (
                <div className={d.capability.unlockRow} onClick={(e) => e.stopPropagation()}>
                    <p className={d.capability.unlockText}>
                        {t('socialAccounts.card.coexistenceHint')}
                        {coexWindow.deadline
                            ? t('socialAccounts.card.coexistenceDeadline', { deadline: coexWindow.deadline.toLocaleString(i18n.language || 'pt-BR') })
                            : ''}
                    </p>
                    <button
                        type="button"
                        className={d.capability.unlockBtn}
                        onClick={() => onLinkChannel(account)}
                    >
                        <Iconify icon="eva:link-2-outline" width={13} />
                        {t('socialAccounts.actions.linkChannel')}
                    </button>
                </div>
            )}
            {coexWindow?.state === 'expired' && !waIncompleto && (
                <div className={d.capability.unlockRow} onClick={(e) => e.stopPropagation()}>
                    <p className={d.capability.unlockText}>
                        {t('socialAccounts.card.expiredHint')}
                    </p>
                    <button type="button" className={d.capability.unlockBtn} onClick={() => abrirWhatsappSignup(navigate)}>
                        <Iconify icon="eva:refresh-outline" width={13} />
                        {t('socialAccounts.actions.reconnectWhatsapp')}
                    </button>
                </div>
            )}
            {!isAds && !isWa && !publish && (
                <div className={d.capability.unlockRow} onClick={(e) => e.stopPropagation()}>
                    {blockedByAppReview(account) ? (
                        <p className={d.capability.unlockText}>
                            {t('socialAccounts.card.appReviewHint')}
                        </p>
                    ) : (
                        <>
                            <p className={d.capability.unlockText}>
                                {t('socialAccounts.card.readOnlyHint')}
                            </p>
                            <button type="button" className={d.capability.unlockBtn} onClick={() => oauth.liberarPublicacao(account)}>
                                <Iconify icon="eva:unlock-outline" width={13} />
                                {t('socialAccounts.actions.unlockPublish')}
                            </button>
                        </>
                    )}
                </div>
            )}
            {META_ADS_DISPONIVEL && ((isAds && !adsOk) || (!isAds && account.provider === 'facebook' && adsScopesMissing.length > 0)) ? (
                <div className={d.capability.unlockRow} onClick={(e) => e.stopPropagation()}>
                    <p className={d.capability.unlockText}>
                        {t('socialAccounts.card.adsScopesHint')}
                    </p>
                    <button type="button" className={d.capability.unlockBtn} onClick={oauth.conectarAnuncios}>
                        <Iconify icon="eva:unlock-outline" width={13} />
                        {t('socialAccounts.actions.updateAdsPermissions')}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

function AccountRow({ view, navigate, oauth, onLinkChannel, syncingId, onSync, onDisconnect }) {
    const { t, i18n } = useTranslation('settings');
    const { account, isAds, isWa, followers, posts, platformIcon, profileHref, statusCfg } = view;
    const showPostsBtn = !isAds && !isWa;
    const canal = getCanalDisplay(view, t);
    const primary = getPrimaryAction(view, { navigate, oauth, onLinkChannel, t })
        || (profileHref ? { label: t('socialAccounts.actions.openProfile'), icon: 'eva:edit-2-outline', onClick: () => navigate(profileHref) } : null);

    return (
        <div
            className={`${GENERIC_TABLE_DESIGN.row.container}${profileHref ? ' cursor-pointer' : ''} min-w-[900px]`}
            onClick={profileHref ? () => navigate(profileHref) : undefined}
        >
            <div className={d.table.colAccount}>
                <div className="relative flex-shrink-0">
                    {account.profile_picture ? (
                        <img src={account.profile_picture} alt={account.account_name} className="w-10 h-10 rounded-lg object-cover" />
                    ) : isAds ? (
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                            <Iconify icon="eva:pricetags-outline" width={18} className="text-muted-foreground" />
                        </div>
                    ) : (
                        <div className="w-10 h-10 rounded-lg bg-foreground text-background flex items-center justify-center text-sm font-extrabold">
                            {(account.account_name || '?').charAt(0).toUpperCase()}
                        </div>
                    )}
                    {platformIcon && (
                        <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full shadow-sm flex items-center justify-center ${platformIcon.className}`}>
                            <Iconify icon={platformIcon.icon} width={10} />
                        </span>
                    )}
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{account.account_name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                        {isAds
                            ? (account.account_info?.act_id || `act_${account.account_id}`)
                            : isWa
                              ? (account.account_info?.displayPhoneNumber || t('socialAccounts.card.pendingNumber'))
                              : (account.account_info?.username ? `@${account.account_info.username}` : account.account_info?.category || account.provider)}
                    </p>
                </div>
            </div>

            <div className={d.table.colCanal}>
                <p className={`${d.table.canalTitle} ${canal.titleClass}`}>{canal.title}</p>
                {canal.caption && <p className={d.table.canalCaption}>{canal.caption}</p>}
            </div>

            <div className={d.table.colStats}>
                {isWa ? (
                    <div>
                        <p className={d.table.statValue}>{account.account_info?.signupMode === 'coexistence' ? t('socialAccounts.stats.coexistenceShort') : t('socialAccounts.stats.cloudShort')}</p>
                        <p className={d.table.statLabel}>{t('socialAccounts.stats.mode')}</p>
                    </div>
                ) : isAds ? (
                    <div>
                        <p className={d.table.statValue}>{formatNumber(Number(account.account_info?.amount_spent || 0) / 100)}</p>
                        <p className={d.table.statLabel}>{t('socialAccounts.stats.spend')}</p>
                    </div>
                ) : (
                    <>
                        <div>
                            <p className={d.table.statValue}>{formatNumber(followers)}</p>
                            <p className={d.table.statLabel}>{t('socialAccounts.stats.followersShort')}</p>
                        </div>
                        <div>
                            <p className={d.table.statValue}>{formatNumber(posts)}</p>
                            <p className={d.table.statLabel}>{t('socialAccounts.stats.postsShort')}</p>
                        </div>
                    </>
                )}
            </div>

            <div className={d.table.colStatus}>
                <StatusChip tone={statusCfg.tone} label={statusCfg.label} size="sm" />
                <p className="text-[11px] text-muted-foreground mt-1">{formatDate(account.last_sync, t, i18n.language)}</p>
            </div>

            <div className={d.table.colActions} onClick={(e) => e.stopPropagation()}>
                {primary && (
                    <button
                        type="button"
                        className={`${primary.tone === 'warning' ? d.accounts.warnBtn : d.accounts.primaryBtn} flex-none max-w-[130px]`}
                        onClick={primary.onClick}
                    >
                        <span className="truncate">{primary.label}</span>
                    </button>
                )}
                {showPostsBtn && (
                    <button
                        type="button"
                        title={t('socialAccounts.header.goToPosts')}
                        className={d.accounts.iconBtn}
                        onClick={() => navigate('/dashboard/marketing/publicacoes')}
                    >
                        <Iconify icon="eva:paper-plane-outline" width={15} />
                    </button>
                )}
                <button
                    type="button"
                    title={t('socialAccounts.actions.syncNow')}
                    className={d.accounts.iconBtn}
                    onClick={() => onSync(account._id)}
                    disabled={syncingId === account._id}
                >
                    <Iconify icon={syncingId === account._id ? 'eos-icons:loading' : 'eva:refresh-outline'} width={15} />
                </button>
                <button
                    type="button"
                    title={t('socialAccounts.actions.disconnect')}
                    className={d.accounts.iconBtnDanger}
                    onClick={() => onDisconnect(account)}
                >
                    <Iconify icon="eva:close-circle-outline" width={16} />
                </button>
            </div>
        </div>
    );
}

export default function ContasSociaisComponent() {
    const { t } = useTranslation(['settings', 'common', 'notifications']);
    const { user, cliente, updateCliente } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fromAnuncios = searchParams.get('from') === 'anuncios';

    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid');
    const [syncingId, setSyncingId] = useState(null);
    const [disconnectTarget, setDisconnectTarget] = useState(null);
    const [disconnecting, setDisconnecting] = useState(false);
    const [oauthSessionId, setOauthSessionId] = useState(null);
    const [oauthReturnTo, setOauthReturnTo] = useState(null);
    const [installedSlugs, setInstalledSlugs] = useState(() => toInstalledSlugSet(cliente?.plugins));
    const [pluginsLoading, setPluginsLoading] = useState(() => !Array.isArray(cliente?.plugins));
    // Fila de contas aguardando o prompt "vincular a um canal" — alimentada
    // tanto pelo clique manual em "Vincular canal" quanto, automaticamente,
    // pelo fim do onboarding (confirmacao do OAuth Meta / retorno do Embedded
    // Signup do WhatsApp). Um _id por vez; o dialogo processa e avanca a fila.
    const [linkQueue, setLinkQueue] = useState([]);
    // meta=ok volta sem dizer quais contas foram conectadas (so a contagem) —
    // sinaliza que, apos o proximo fetchAll, a conta mais recente sem canal
    // deve entrar na fila do prompt.
    const [pendingDirectLinkPrompt, setPendingDirectLinkPrompt] = useState(false);

    useEffect(() => {
        if (!user?.clienteId) return undefined;
        let cancelled = false;
        setPluginsLoading(true);
        pluginsService.listarPorCliente(user.clienteId)
            .then((res) => {
                if (cancelled) return;
                const slugs = extractInstalledSlugs(res);
                setInstalledSlugs(slugs);
                const next = [...slugs];
                const atual = Array.isArray(cliente?.plugins)
                    ? [...cliente.plugins].map(String).sort().join('|')
                    : '';
                const novo = [...next].map(String).sort().join('|');
                if (atual !== novo) {
                    updateCliente({ plugins: next });
                }
            })
            .catch(() => {
                if (!cancelled) setInstalledSlugs((atual) => atual ?? new Set());
            })
            .finally(() => {
                if (!cancelled) setPluginsLoading(false);
            });
        return () => { cancelled = true; };
        // updateCliente muda a cada render do AuthContext — nao entra nas deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.clienteId]);

    // Retorna a lista recem-buscada (nao so grava no state): quem chama logo
    // apos um OAuth precisa decidir, no mesmo tick, quais contas novas ainda
    // nao tem canal — esperar o proximo render para ler `accounts` chegaria
    // atrasado.
    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            // includePending: conexoes de WhatsApp interrompidas no meio
            // precisam aparecer aqui — e' a unica tela de onde da' para
            // concluir ou descartar.
            const accountsRes = await mktAccountsService.listar(user.clienteId, { includePending: true });
            const lista = accountsRes?.data || [];
            setAccounts(lista);
            return lista;
        } catch (err) {
            console.error('Erro ao carregar contas:', err);
            toast({ title: t('notifications:toast.error'), description: t('socialAccounts.toast.loadError'), variant: 'destructive' });
            return [];
        } finally {
            setLoading(false);
        }
    }, [user.clienteId, t]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const meta = params.get('meta');
        if (!meta) return;

        const returnTo = consumeOAuthReturnPath();

        if (meta === 'selecionar') {
            const session = params.get('session');
            if (session) {
                setOauthSessionId(session);
                setOauthReturnTo(returnTo || (fromAnuncios ? '/dashboard/marketing/anuncios' : null));
            } else {
                toast({
                    title: t('socialAccounts.toast.invalidSession'),
                    description: t('socialAccounts.toast.invalidSessionDesc'),
                    variant: 'destructive'
                });
            }
            window.history.replaceState({}, '', window.location.pathname + (fromAnuncios ? '?from=anuncios' : ''));
            return;
        }

        if (meta === 'ok') {
            toast({
                title: t('socialAccounts.toast.accountsConnected'),
                description: t('socialAccounts.toast.accountsAvailable', { count: Number(params.get('contas') || 0) })
            });
            if (returnTo) {
                window.history.replaceState({}, '', window.location.pathname);
                navigate(returnTo);
                return;
            }
            // Fim do onboarding sem passar pela selecao (MetaAccountSelect) —
            // ex.: Instagram Login direto. Assim que as contas recarregarem,
            // oferece vincular a mais recente que ainda nao tem canal.
            setPendingDirectLinkPrompt(true);
        } else if (meta === 'sem_paginas') {
            toast({
                title: t('socialAccounts.toast.noPagesOrAds'),
                description: t('socialAccounts.toast.noPagesOrAdsDesc'),
                variant: 'destructive'
            });
        } else if (meta === 'erro') {
            toast({
                title: t('socialAccounts.toast.connectFailed'),
                description: params.get('motivo') || t('socialAccounts.toast.connectFailedDesc'),
                variant: 'destructive'
            });
        }

        window.history.replaceState({}, '', window.location.pathname);
    }, [navigate, fromAnuncios, t]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // Adiciona _ids ao final da fila do prompt de vinculo, sem duplicar.
    const enqueueLinkTargets = useCallback((ids) => {
        const validIds = (ids || []).filter(Boolean).map(String);
        if (validIds.length === 0) return;
        setLinkQueue((prev) => {
            const next = [...prev];
            validIds.forEach((id) => { if (!next.includes(id)) next.push(id); });
            return next;
        });
    }, []);

    // Entrada manual (botao "Vincular canal" no card): so oferece o dialogo
    // para os providers que o backend sabe vincular — Mercado Livre e Ads nao
    // tem esse endpoint, entao caem no destino antigo (pagina de Canais).
    const openLinkDialog = (account) => {
        if (!account?._id) return;
        if (!CANAL_PLATFORM_POR_PROVIDER[account.provider]) {
            navigate(CANAIS_PATH);
            return;
        }
        enqueueLinkTargets([account._id]);
    };
    const dequeueLinkDialog = () => setLinkQueue((prev) => prev.slice(1));
    const handleAccountLinked = async () => {
        await fetchAll();
        dequeueLinkDialog();
    };

    // accounts vem ordenado por created_at desc (ver mkt.accounts.controller
    // listAccounts) — o primeiro candidato ja e' o mais recente.
    useEffect(() => {
        if (!pendingDirectLinkPrompt || loading) return;
        setPendingDirectLinkPrompt(false);
        const candidato = accounts.find(
            (a) => CANAL_PLATFORM_POR_PROVIDER[a.provider] && a.status === 'active' && !a.canal_vinculado
        );
        if (candidato) enqueueLinkTargets([candidato._id]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingDirectLinkPrompt, loading, accounts]);

    // Remove da fila um _id que nao existe mais na lista carregada (conta
    // desconectada, ou candidato de uma sessao antiga) — sem isso o dialogo
    // ficaria "aberto" com account undefined.
    useEffect(() => {
        if (loading || linkQueue.length === 0) return;
        if (!accounts.some((a) => String(a._id) === linkQueue[0])) {
            setLinkQueue((prev) => prev.slice(1));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, accounts, linkQueue]);

    // Toda abertura de OAuth envia o grupo pedido + a uniao do que ja foi
    // concedido nas contas existentes do mesmo app: rerequest nao revoga
    // nada, mas reenviar a uniao reexibe escopos negados e garante que o
    // proximo debug_token nao "perca" nenhum concedido.
    const iniciarOAuthFacebook = (grupo, returnTo) => {
        abrirMetaOAuth(user.clienteId, {
            scopes: unirEscopos(escoposConcedidos(accounts, 'facebook'), grupo),
            returnTo,
        });
    };
    const iniciarOAuthInstagram = (grupo) => {
        abrirInstagramOAuth(user.clienteId, {
            scopes: unirEscopos(escoposConcedidos(accounts, 'instagram'), grupo),
        });
    };

    const conectarMessenger = () => iniciarOAuthFacebook(ESCOPOS_MESSENGER);
    const conectarFacebook = () => iniciarOAuthFacebook(ESCOPOS_FACEBOOK);
    const conectarAnuncios = () => iniciarOAuthFacebook(
        ESCOPOS_ANUNCIOS,
        fromAnuncios ? '/dashboard/marketing/anuncios' : undefined
    );
    const atualizarPermissoesFacebook = () => iniciarOAuthFacebook(
        ESCOPOS_FACEBOOK_TODOS,
        fromAnuncios ? '/dashboard/marketing/anuncios' : undefined
    );
    const conectarDirectInstagram = () => iniciarOAuthInstagram(ESCOPOS_DIRECT_INSTAGRAM);
    const conectarInstagram = () => iniciarOAuthInstagram(ESCOPOS_INSTAGRAM);
    // Mercado Livre: diferente dos fluxos Meta, quem monta a URL de autorização é
    // o backend (state assinado + PKCE), então é uma chamada assíncrona que pode
    // falhar antes de sair da tela — sem o catch o erro sumiria no console.
    const conectarMercadoLivre = async () => {
        try {
            await abrirMercadoLivreOAuth(user.clienteId);
        } catch (err) {
            console.error('Erro ao iniciar OAuth do Mercado Livre:', err);
            toast({
                title: t('notifications:toast.error'),
                description: err?.message || 'Não foi possível iniciar a conexão com o Mercado Livre',
                variant: 'destructive',
            });
        }
    };
    // Grupo por provider da conta que precisa publicar.
    const liberarPublicacao = (account) => (
        account.provider === 'facebook' ? conectarFacebook() : conectarInstagram()
    );

    const oauth = {
        conectarMessenger, conectarDirectInstagram, conectarInstagram, conectarFacebook,
        conectarAnuncios, atualizarPermissoesFacebook, liberarPublicacao,
        conectarMercadoLivre,
    };

    const handleSync = async (accountId) => {
        setSyncingId(accountId);
        try {
            await mktAccountsService.sincronizar(accountId);
            toast({ title: t('socialAccounts.toast.accountSynced') });
            fetchAll();
        } catch (err) {
            console.error('Erro ao sincronizar conta:', err);
            toast({ title: t('notifications:toast.error'), description: err?.message || t('socialAccounts.toast.syncError'), variant: 'destructive' });
        } finally {
            setSyncingId(null);
        }
    };

    const handleDisconnectConfirm = async () => {
        if (!disconnectTarget) return;
        setDisconnecting(true);
        try {
            await mktAccountsService.deletar(user.clienteId, disconnectTarget._id);
            setAccounts((prev) => prev.filter((a) => a._id !== disconnectTarget._id));
            toast({ title: t('socialAccounts.toast.accountDisconnected') });
            setDisconnectTarget(null);
            fetchAll();
        } catch (err) {
            console.error('Erro ao desconectar conta:', err);
            toast({ title: t('notifications:toast.error'), description: err?.message || t('socialAccounts.toast.disconnectError'), variant: 'destructive' });
        } finally {
            setDisconnecting(false);
        }
    };

    const hasAccounts = accounts.length > 0;
    const accountViews = useMemo(() => accounts.map((a) => buildAccountView(a, t)), [accounts, t]);
    const readOnlyAccounts = accounts.filter((a) => a.provider !== 'meta_ads' && !isSemPublicacao(a) && !canPublish(a));
    const reconectaveis = readOnlyAccounts.filter((a) => !blockedByAppReview(a));

    const hasAdsReady = accounts.some((a) => canReadAds(a));
    const facebookOrAdsAccounts = accounts.filter((a) => a.provider === 'facebook' || a.provider === 'meta_ads');
    // Só propõe a conexão de anúncios quando há evidência de que o cliente a
    // quer: ou veio de Marketing → Anúncios (?from=anuncios), ou já tem conta
    // Facebook/Ads sem os escopos. Para quem nunca pediu anúncios, o card
    // "Anúncios" do empty state e a opção no dropdown já bastam — o alerta
    // seria só ruído. Com a permissão bloqueada na Meta não há o que propor.
    const needsAdsReconnect = META_ADS_DISPONIVEL
        && !hasAdsReady
        && (fromAnuncios || facebookOrAdsAccounts.length > 0);
    const scopesMissingOnExisting = facebookOrAdsAccounts.some((a) => missingAdsScopes(a).length > 0);
    const hasAlerts = needsAdsReconnect || readOnlyAccounts.length > 0;
    const pendenciasCount = accountViews.filter((v) => !v.isAds && !v.canalVinculado).length;

    const finishOAuthSelection = async (connected = [], message) => {
        setOauthSessionId(null);
        toast({
            title: t('socialAccounts.toast.accountsConnected'),
            description: message || t('socialAccounts.toast.accountsAdded', { count: connected.length })
        });
        const freshAccounts = await fetchAll();
        if (oauthReturnTo) {
            const dest = oauthReturnTo;
            setOauthReturnTo(null);
            navigate(dest);
            return;
        }
        // Fim do onboarding: das contas que acabaram de ser confirmadas, quais
        // ja podem receber um canal e ainda nao tem um — usa a lista recem-
        // buscada (com canal_vinculado calculado) em vez de `connected`
        // (payload cru do confirm, sem esse campo).
        const connectedIds = new Set(connected.map((a) => String(a._id)));
        const semCanal = freshAccounts
            .filter((a) => connectedIds.has(String(a._id)) && CANAL_PLATFORM_POR_PROVIDER[a.provider] && !a.canal_vinculado)
            .map((a) => a._id);
        enqueueLinkTargets(semCanal);
    };

    const cancelOAuthSelection = () => {
        setOauthSessionId(null);
        setOauthReturnTo(null);
        toast({ title: t('socialAccounts.toast.selectionCancelled'), description: t('socialAccounts.toast.selectionCancelledDesc') });
    };

    const linkTargetAccount = linkQueue.length > 0
        ? accounts.find((a) => String(a._id) === linkQueue[0]) || null
        : null;

    if (oauthSessionId) {
        return (
            <div className={d.pageShell} style={{ fontFamily: d.fontStack }}>
                <MetaAccountSelect
                    clienteId={user.clienteId}
                    userId={user.id || user._id}
                    sessionId={oauthSessionId}
                    preferAds={Boolean(fromAnuncios || oauthReturnTo?.includes('/anuncios'))}
                    onConnected={finishOAuthSelection}
                    onCancel={cancelOAuthSelection}
                />
            </div>
        );
    }

    return (
        <div className={d.pageShell} style={{ fontFamily: d.fontStack }}>
            <div className={d.stickyHeader}>
                <div className={d.container}>
                    <div className={d.pageHeader.container}>
                        <div className={d.pageHeader.identity}>
                            <div className="min-w-0">
                                <div className={d.pageHeader.titleRow}>
                                    <h1 className={d.pageHeader.title}>{t('socialAccounts.header.title')}</h1>
                                    {!loading && (
                                        <span className={d.countBadge}>
                                            {hasAccounts
                                                ? t('socialAccounts.header.connectedCount', { count: accounts.length })
                                                : t('socialAccounts.header.connectedNone')}
                                            {pendenciasCount > 0 ? ` · ${t('socialAccounts.header.pendencias', { count: pendenciasCount })}` : ''}
                                        </span>
                                    )}
                                </div>
                                <p className={d.pageHeader.subtitle}>
                                    {t('socialAccounts.header.subtitle')}
                                </p>
                            </div>
                        </div>
                        <div className={d.pageHeader.actions}>
                            {needsAdsReconnect && (
                                <button type="button" onClick={conectarAnuncios} className={d.pageHeader.actionBtnOutline}>
                                    {t('socialAccounts.header.updateMetaPermissions')}
                                </button>
                            )}
                            {hasAccounts && (
                                <Tabs value={viewMode} onValueChange={setViewMode}>
                                    <TabsList className={d.viewToggle.list}>
                                        <TabsTrigger value="grid" className={d.viewToggle.trigger}>
                                            {t('socialAccounts.view.grid')}
                                        </TabsTrigger>
                                        <TabsTrigger value="list" className={d.viewToggle.trigger}>
                                            {t('socialAccounts.view.list')}
                                        </TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            )}
                            <ConnectAccountMenu
                                t={t}
                                navigate={navigate}
                                oauthActions={oauth}
                                triggerClassName={d.pageHeader.actionBtn}
                                installedSlugs={installedSlugs}
                                pluginsLoading={pluginsLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={`${d.container} pt-6 ${d.pageBody} flex flex-col gap-3.5`}>

            {loading && <MktLoading />}

            {!loading && hasAlerts && (
                <div className={d.alertRow.list}>
                    {fromAnuncios && needsAdsReconnect && (
                        <AlertRow
                            tone="urgent"
                            title={t('socialAccounts.alerts.adsLoginTitle')}
                            subtitle={(
                                <Trans
                                    t={t}
                                    i18nKey="socialAccounts.alerts.adsLoginSubtitle"
                                    components={{ bold: <strong /> }}
                                />
                            )}
                            ctaLabel={t('socialAccounts.alerts.adsLoginCta')}
                            ctaIcon="eva:log-in-outline"
                            onCta={conectarAnuncios}
                        />
                    )}
                    {!fromAnuncios && needsAdsReconnect && (
                        <AlertRow
                            tone="warning"
                            title={scopesMissingOnExisting || facebookOrAdsAccounts.length > 0
                                ? t('socialAccounts.alerts.adsPermissionsOutdated')
                                : t('socialAccounts.alerts.adsNotConnected')}
                            subtitle={hasAccounts
                                ? t('socialAccounts.alerts.adsPermissionsSubtitleHasAccounts')
                                : t('socialAccounts.alerts.adsPermissionsSubtitleEmpty')}
                            ctaLabel={hasAccounts ? t('socialAccounts.actions.updatePermissions') : t('socialAccounts.menu.facebook')}
                            ctaIcon="eva:unlock-outline"
                            onCta={conectarAnuncios}
                        />
                    )}
                    {readOnlyAccounts.length > 0 && (
                        <AlertRow
                            tone="warning"
                            title={readOnlyAccounts.length === 1
                                ? t('socialAccounts.alerts.publishDeniedOne', { name: readOnlyAccounts[0].account_name })
                                : t('socialAccounts.alerts.publishDeniedMany', { count: readOnlyAccounts.length })}
                            subtitle={reconectaveis.length > 0
                                ? t('socialAccounts.alerts.publishDeniedReconnect')
                                : t('socialAccounts.alerts.publishDeniedAppReview')}
                            ctaLabel={reconectaveis.length > 0 ? t('socialAccounts.actions.unlockPublish') : null}
                            ctaIcon="eva:unlock-outline"
                            onCta={() => liberarPublicacao(reconectaveis.find((a) => a.provider === 'facebook') || reconectaveis[0])}
                        />
                    )}
                </div>
            )}

            {!loading && !hasAccounts && (
                <AccountsEmptyState
                    t={t}
                    navigate={navigate}
                    oauthActions={oauth}
                    installedSlugs={installedSlugs}
                    pluginsLoading={pluginsLoading}
                />
            )}

            {!loading && hasAccounts && viewMode === 'grid' && (
                <div className={d.accounts.grid}>
                    {accountViews.map((view) => (
                        <AccountCard
                            key={view.account._id}
                            view={view}
                            navigate={navigate}
                            oauth={oauth}
                            onLinkChannel={openLinkDialog}
                            syncingId={syncingId}
                            onSync={handleSync}
                            onDisconnect={setDisconnectTarget}
                        />
                    ))}
                </div>
            )}

            {!loading && hasAccounts && viewMode === 'list' && (
                <GenericTable
                    headLabels={[
                        { id: 'account', label: t('socialAccounts.table.account'), className: d.table.colAccount },
                        { id: 'canal', label: t('socialAccounts.table.channel'), className: d.table.colCanal },
                        { id: 'stats', label: t('socialAccounts.table.reach'), className: d.table.colStats },
                        { id: 'status', label: t('socialAccounts.table.status'), className: d.table.colStatus },
                        { id: 'actions', label: t('socialAccounts.table.actions'), className: d.table.colActions },
                    ]}
                >
                    {accountViews.map((view) => (
                        <AccountRow
                            key={view.account._id}
                            view={view}
                            navigate={navigate}
                            oauth={oauth}
                            onLinkChannel={openLinkDialog}
                            syncingId={syncingId}
                            onSync={handleSync}
                            onDisconnect={setDisconnectTarget}
                        />
                    ))}
                </GenericTable>
            )}

            {!loading && hasAccounts && (
                <div>
                    <div className={d.table.footer}>
                        <span className={d.table.footerText}>{t('socialAccounts.table.footer')}</span>
                        <span className={d.table.manageLink} onClick={() => navigate(CANAIS_PATH)}>{t('socialAccounts.actions.manageChannels')}</span>
                    </div>
                    <div className={d.table.hintRow}>
                        <Iconify icon="eva:info-outline" width={14} />
                        {t('socialAccounts.table.hint')}
                    </div>
                </div>
            )}

            <Dialog open={Boolean(disconnectTarget)} onOpenChange={(v) => { if (!v) setDisconnectTarget(null); }}>
                <DialogContent className="sm:max-w-[420px] rounded-2xl border-0 shadow-2xl bg-card p-0 overflow-hidden">
                    <div className="p-6 pb-4 border-b border-border bg-red-50/50 dark:bg-red-500/10">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-semibold text-foreground flex items-center gap-2">
                                <Iconify icon="mdi:alert-circle-outline" width={24} height={24} className="text-red-500" />
                                {t('socialAccounts.disconnect.title')}
                            </DialogTitle>
                        </DialogHeader>
                    </div>
                    <div className="p-6 space-y-3">
                        <p className="text-muted-foreground text-sm">
                            <Trans
                                t={t}
                                i18nKey="socialAccounts.disconnect.confirm"
                                values={{ name: disconnectTarget?.account_name }}
                                components={{ name: <span className="font-semibold text-foreground" /> }}
                            />
                        </p>
                        <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/25">
                            <Iconify icon="eva:trash-2-outline" width={14} className="mt-0.5 flex-shrink-0" />
                            <span>{t('socialAccounts.disconnect.warning')}</span>
                        </div>
                        {disconnectTarget?.canal_vinculado && (
                            <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/25">
                                <Iconify icon="eva:link-2-outline" width={14} className="mt-0.5 flex-shrink-0" />
                                <span>
                                    <Trans
                                        t={t}
                                        i18nKey="socialAccounts.disconnect.channelWarning"
                                        values={{ name: disconnectTarget.canal_vinculado.nome }}
                                        components={{ name: <span className="font-semibold" /> }}
                                    />
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end gap-3 p-6 pt-2 border-t border-border bg-muted/30">
                        <Button variant="ghost" onClick={() => setDisconnectTarget(null)} disabled={disconnecting} className="rounded-xl">
                            {t('common:buttons.cancel')}
                        </Button>
                        <Button variant="destructive" onClick={handleDisconnectConfirm} disabled={disconnecting} className="rounded-xl px-6">
                            {disconnecting ? t('socialAccounts.disconnect.disconnecting') : t('socialAccounts.actions.disconnect')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <LinkChannelDialog
                account={linkTargetAccount}
                clienteId={user.clienteId}
                userId={user.id || user._id}
                position={linkTargetAccount ? 1 : 0}
                total={linkTargetAccount ? linkQueue.length : 0}
                onClose={dequeueLinkDialog}
                onLinked={handleAccountLinked}
            />
            </div>
        </div>
    );
}
